import localOutputs from '@/tools/generated/local-tool-outputs'
import rawOutputs from '@/tools/generated/tool-outputs'
import { resolveToolId } from '@/tools/tool-ids'
import type { ToolConfig } from '@/tools/types'

/**
 * Declared output shapes for built-in tools, read without importing the
 * executable registry. See `@/tools/metadata` for the rationale.
 *
 * Kept apart from the rest of the metadata because outputs are roughly two
 * thirds of the generated data and only block-output inference consumes them —
 * callers that need params or an existence check should import `@/tools/metadata`
 * and never load this module.
 *
 * The backing data is generated by `scripts/sync-tool-metadata.ts` and verified
 * in CI by `bun run tool-metadata:check`. Never hand-edit it.
 */
type ToolOutputs = NonNullable<ToolConfig['outputs']>

/**
 * Annotated rather than inferred: letting TypeScript infer a literal type for a
 * 4 MB JSON import makes every downstream file that touches it dramatically more
 * expensive to typecheck.
 */
const outputs: Record<string, ToolOutputs> = { ...rawOutputs, ...localOutputs } as Record<
  string,
  ToolOutputs
>

/** Adds the catalogue's entries; `lib/ondit/integration-catalogue` calls this once. */
export function extendToolOutputs(entries: Record<string, unknown>): void {
  Object.assign(outputs, entries as Record<string, ToolOutputs>)
}

/**
 * Declared outputs for a built-in tool, or `undefined` if it declares none.
 *
 * `Object.hasOwn` rather than a bare lookup — see `getToolMetadata` for why.
 */
export function getToolOutputsMetadata(toolId: string): ToolOutputs | undefined {
  const resolved = resolveToolId(toolId)
  return Object.hasOwn(outputs, resolved) ? outputs[resolved] : undefined
}
